import { Injectable } from '@angular/core';
import { AbstractWidgetService } from '../../services/abstract-widget.service';
import { DocumentEditorActionsType, DocumentInfo, TemplateActionArgs, UiWidget } from '@pt/pt-models';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { ApplicationService, BusinessProjectManagementService, DownloadService } from '@pt/api-client';
import { Application } from 'it/primatoo/openapi/api/org/schemas/application/Application';
import {
  WidgetAppSummaryV0ValueAppDto,
  WidgetAppSummaryV0ValueDto
} from './v0/models/widget-app-summary-v0-value-app.dto';
import { WidgetActionsUtils } from '../../utils/widget-actions-utils';
import { AssessmentCategory } from 'it/primatoo/openapi/api/org/schemas/appassessment/AssessmentCategory';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import * as NgrxWidgetActions from '../../store/actions/widget.actions';
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';

@Injectable({
  providedIn: 'root'
})
export class WidgetAppSummaryService extends AbstractWidgetService {

  constructor(public store: Store,
              public applicationService: ApplicationService,
              public downloadService: DownloadService,
              public translateService: TranslateService) {
    super(store, translateService);
  }

  /**
   * TRIGGER
   * @param context
   * @param widgets
   * @param widgetId
   * @param action
   * @param args
   * @param queryResponse
   */
  execTrigger(context: GetChapterResponse, widgets: UiWidget[], widgetId: number): UiWidget | null {
    const widget = widgets?.find(w => w.internalId === widgetId);
    this.unsupportedTrigger(widget);
    return null;
  }

  /**
   *
   * @param sourceWidget
   * @param targetWidget
   * @param action
   * @param args
   * @param queryResponse
   */
  execAsyncTrigger(sourceWidget: UiWidget, targetWidget: UiWidget, action: DocumentEditorActionsType, args: TemplateActionArgs, docInfo: DocumentInfo,
                   targetChapterSelector: ChapterSelector): void {

    if (targetWidget?.version !== null && targetWidget?.version !== undefined) {
      switch (targetWidget.version) {
        case 0:
          this.execTriggerV2V0(sourceWidget, targetWidget, action, args, docInfo, targetChapterSelector);
          break;
        default:
          this.invalidWidgetVersion(sourceWidget);
      }
    }
    this.unsupportedTrigger(sourceWidget);
  }

  private execTriggerV2V0(sourceWidget: UiWidget, targetWidget: UiWidget, action: DocumentEditorActionsType, args: TemplateActionArgs, docInfo: DocumentInfo, targetChapterSelector: ChapterSelector): UiWidget | null {

    switch (action) {
      case DocumentEditorActionsType.APP_VALUE:
        this.validWidgetAction(sourceWidget, action);
        return this.appValueAction(sourceWidget, targetWidget, args, docInfo, targetChapterSelector);

      default:
        this.invalidWidgetAction(sourceWidget, action);
    }
    return null;

  }


  private appValueAction(sourceWidget: UiWidget, targetWidget: UiWidget, args: TemplateActionArgs, docInfo: DocumentInfo, targetChapterSelector: ChapterSelector) {
    const widget = JSON.parse(JSON.stringify(sourceWidget)) as UiWidget;
    const sourceValue: string[] = WidgetActionsUtils.execJsonPath(args?.jsonPath?.source, [widget]);
    /*
     ["tes2302211252xke","wha2307270822yoa"]

      Confidentiality -> R
      Integrity -> I
      Availability -> D
     */

    if (sourceValue?.length > 0) {
      this.applicationService.findAll().subscribe((s: Application[]) => {
        const selectedApp: Application[] = s.filter(app => sourceValue.includes(app.uuid));
        const targetValue: WidgetAppSummaryV0ValueDto = targetWidget.value ? JSON.parse(JSON.stringify(targetWidget.value)) : { app: [] };
        targetValue.app = [];
        targetValue.app = selectedApp.map((a: Application) => {
          const appAlreadySaved = targetValue.app.find((ta) => ta.applicationUuid === a.uuid);
          if (appAlreadySaved) {
            return appAlreadySaved;
          } else {
            const response: WidgetAppSummaryV0ValueAppDto = {
              applicationUuid: a.uuid,
              unsupported: a.assessmentCategory === AssessmentCategory.NOT_SUPPORTED,
              scenarioRLevel: a.confidentialityViolationLevel,
              scenarioILevel: a.integrityCompromiseLevel,
              scenarioDLevel: a.availabilityLossLevel,
              scenarioRImplValue: a.confidentialityViolationImplValue,
              scenarioIImplValue: a.integrityCompromiseImplValue,
              scenarioDImplValue: a.availabilityLossImplValue
            };
            return response;
          }
        });
        targetWidget.value = targetValue;
        const request: UpsertWidgetsRequest = WidgetActionsUtils.generateWidgetsUpsertRequest([targetWidget], targetChapterSelector);
        const service: BusinessProjectManagementService = this.downloadService.getDocumentEditorTypeService(docInfo.docType);

        if (service) {
          service.upsertWidgets(docInfo.docId, request).subscribe(() => {
            this.store.dispatch(NgrxWidgetActions.disableWarnings({ request: { disabledWarnings: [TreatmentWarning.APP_LIST_CHANGED] } }));
          });
        }
      });
    } else {
      const targetValue: WidgetAppSummaryV0ValueDto = targetWidget.value;
      targetValue.app = [];
      const request: UpsertWidgetsRequest = WidgetActionsUtils.generateWidgetsUpsertRequest([targetWidget], targetChapterSelector);
      const service: BusinessProjectManagementService = this.downloadService.getDocumentEditorTypeService(docInfo.docType);
      if (service) {
        service.upsertWidgets(docInfo.docId, request).subscribe(() => {
          this.store.dispatch(NgrxWidgetActions.disableWarnings({ request: { disabledWarnings: [TreatmentWarning.APP_LIST_CHANGED] } }));
          // setTimeout(() => {
          //   this.store.dispatch(NgrxWidgetActions.reloadWidgetAfterSave({widgets: [widget]}));
          // })
        });
      }
    }
    return null;
  }

  /**
   * INITIALIZATION
   * @param context
   * @param widgets
   * @param widgetId
   * @param action
   * @param args
   */
  initialization(context: GetChapterResponse, widgets: UiWidget[], widgetId: number): UiWidget | null {
    const widget = widgets?.find(w => w.internalId === widgetId);
    this.unsupportedInitialization(widget);
    return null;
  }
}
